// Parity P1 wave 1: the blocks P1 owns, registered in the task block registry (hybrid design
// §3.3 block list: description, checklists, subtasks, dependencies, links, files, time, metrics).
// Field blocks (`field:<id>`, `fields:rest`) are not registered: the layout renderer draws them
// from the board's fields. Other packages register theirs the same way (P2 conversation, P4
// mirrors, P5 playbook); an id the layout names but no package registered is skipped.
import { ChecklistsSection } from "../checklists";
import { FilesSection } from "../files-section";
import { TaskLinksSection } from "../task-links";
import { TaskMetrics } from "../task-metrics";
import { TaskDescription } from "../task-page/description";
import { DependenciesSection, SubtasksSection } from "../task-sections";
import { TimeSection } from "../task-time";
import { useTaskHost } from "./host-context";
import { registerTaskBlock, type TaskBlock, type TaskBlockProps, taskBlock } from "./registry";

function Description({ api, company, task, can }: TaskBlockProps) {
  const { people } = useTaskHost();
  return (
    <TaskDescription api={api} company={company} task={task} people={people} canEdit={can.edit} />
  );
}

function Checklists({ api, company, task }: TaskBlockProps) {
  const { people } = useTaskHost();
  return <ChecklistsSection api={api} company={company} task={task} people={people} />;
}

function Subtasks({ api, company, task, board, onOpenTask }: TaskBlockProps) {
  const { people } = useTaskHost();
  return (
    <SubtasksSection
      api={api}
      company={company}
      task={task}
      board={board}
      onOpenTask={onOpenTask}
      people={people}
    />
  );
}

function Dependencies({ api, company, task, onOpenTask }: TaskBlockProps) {
  const { titleOf } = useTaskHost();
  return (
    <DependenciesSection
      api={api}
      company={company}
      task={task}
      titleOf={titleOf}
      onOpenTask={onOpenTask}
    />
  );
}

function Links({ api, company, task, onOpenTask }: TaskBlockProps) {
  return <TaskLinksSection api={api} company={company} task={task} onOpenTask={onOpenTask} />;
}

function Files({ api, company, task }: TaskBlockProps) {
  return <FilesSection api={api} company={company} task={task} />;
}

function Time({ api, company, task }: TaskBlockProps) {
  return <TimeSection api={api} company={company} taskId={task.id} />;
}

function Metrics({ api, company, task, can }: TaskBlockProps) {
  return <TaskMetrics api={api} company={company} taskId={task.id} canEdit={can.edit} />;
}

export const P1_BLOCKS: TaskBlock[] = [
  {
    id: "description",
    title: "Description",
    placement: "body",
    order: 10,
    isEmpty: (t) => !t.page_id,
    Component: Description,
  },
  {
    id: "checklists",
    title: "Checklists",
    placement: "body",
    order: 20,
    isEmpty: (t) => t.checklists.length === 0,
    Component: Checklists,
  },
  {
    id: "subtasks",
    title: "Subtasks",
    placement: "body",
    order: 30,
    isEmpty: () => false,
    Component: Subtasks,
  },
  {
    id: "dependencies",
    title: "Dependencies",
    placement: "body",
    order: 40,
    isEmpty: (t) => t.dependencies.length === 0,
    Component: Dependencies,
  },
  {
    id: "links",
    title: "Links",
    placement: "body",
    order: 50,
    isEmpty: () => false,
    Component: Links,
  },
  {
    id: "files",
    title: "Files",
    placement: "body",
    order: 60,
    isEmpty: () => false,
    Component: Files,
  },
  {
    id: "time",
    title: "Time",
    placement: "body",
    order: 70,
    isEmpty: () => false,
    Component: Time,
  },
  {
    id: "metrics",
    title: "Metrics",
    placement: "body",
    order: 80,
    isEmpty: () => false,
    Component: Metrics,
  },
];

/** Registers P1's blocks once (module import; a hot reload or a test re-import is a no-op). */
export function registerP1Blocks() {
  for (const b of P1_BLOCKS) if (!taskBlock(b.id)) registerTaskBlock(b);
}

registerP1Blocks();
